import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Check, X, Handshake, Banknote, ShoppingCart, Loader2 } from "lucide-react";

const SECTIONS = [
  { key: "deals", label: "Compras con créditos", icon: ShoppingCart },
  { key: "admin", label: "Tratos con admin", icon: Handshake },
  { key: "payouts", label: "Cobros", icon: Banknote },
];

const Card = ({ children, testid }) => (
  <div data-testid={testid} className="rounded-lg border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
    style={{ borderColor: "#1E293B", backgroundColor: "#111827" }}>
    {children}
  </div>
);

export default function MarketAdmin() {
  const [section, setSection] = useState("deals");
  const [status, setStatus] = useState("PENDIENTE");
  const qc = useQueryClient();

  const path = { deals: "market/deals", admin: "market/admin-deals", payouts: "payouts" }[section];
  const { data, isLoading } = useQuery({
    queryKey: [section, status],
    queryFn: async () => (await api.get(`/admin/${path}?status=${status}`)).data,
  });

  const act = async (url, okMsg, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await api.post(url);
      toast.success(okMsg);
      qc.invalidateQueries({ queryKey: [section] });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const rows = data?.deals || data?.payouts || [];

  return (
    <Layout>
      <div className="p-8">
      <PageHeader title="Mercado" subtitle="Aprueba compras con créditos, atiende tratos y paga cobros" />

      <div className="flex flex-wrap gap-2 mb-4">
        {SECTIONS.map((s) => (
          <Button key={s.key} data-testid={`market-section-${s.key}`} onClick={() => setSection(s.key)}
            className={section === s.key ? "bg-amber-600 hover:bg-amber-500 text-white"
                                         : "bg-slate-800 hover:bg-slate-700 text-slate-200"}>
            <s.icon className="w-4 h-4 mr-1.5" /> {s.label}
          </Button>
        ))}
        <div className="flex-1" />
        {["PENDIENTE", "TODOS"].map((st) => (
          <Button key={st} data-testid={`market-status-${st}`} onClick={() => setStatus(st)}
            className={status === st ? "bg-slate-700 text-white" : "bg-slate-900 text-slate-400"}>
            {st === "PENDIENTE" ? "Pendientes" : "Todos"}
          </Button>
        ))}
      </div>

      {isLoading && <Loader2 className="w-6 h-6 animate-spin text-amber-400" />}
      {!isLoading && rows.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="market-empty">Nada por aquí.</p>
      )}

      <div className="space-y-3">
        {section === "deals" && rows.map((d) => (
          <Card key={d.deal_id} testid={`admin-deal-${d.deal_id}`}>
            <div>
              <div className="text-white font-medium">{d.title}</div>
              <div className="text-xs text-slate-400 mt-1">
                Comprador: {d.buyer_user?.name} · pagó {d.buyer_paid} créditos ·
                {" "}el vendedor {d.seller_user?.name} recibe {d.seller_credits}
              </div>
              <div className="text-xs mt-1 text-slate-500">{d.status} · {(d.created_at || "").slice(0, 16).replace("T", " ")}</div>
            </div>
            {d.status === "PENDIENTE" && (
              <div className="flex gap-2 shrink-0">
                <Button data-testid={`deal-approve-${d.deal_id}`}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  onClick={() => act(`/admin/market/deals/${d.deal_id}/approve`, "Trato aprobado y vendedor pagado", `¿Aprobar «${d.title}»? Se le abonarán ${d.seller_credits} créditos al vendedor.`)}>
                  <Check className="w-4 h-4 mr-1" /> Aprobar
                </Button>
                <Button data-testid={`deal-reject-${d.deal_id}`}
                  className="bg-rose-600 hover:bg-rose-500 text-white"
                  onClick={() => act(`/admin/market/deals/${d.deal_id}/reject`, "Trato rechazado y créditos devueltos", `¿Rechazar «${d.title}»? Se le devolverán ${d.buyer_paid} créditos al comprador.`)}>
                  <X className="w-4 h-4 mr-1" /> Rechazar
                </Button>
              </div>
            )}
          </Card>
        ))}

        {section === "admin" && rows.map((d) => (
          <Card key={d.deal_id} testid={`admin-mediation-${d.deal_id}`}>
            <div>
              <div className="text-white font-medium">{d.title}</div>
              <div className="text-xs text-slate-400 mt-1">
                ${Number(d.price).toLocaleString()} · vendedor {d.seller_name} ·
                {" "}lo pide {d.requester_name} ({d.requester_email || d.requested_by})
              </div>
              <div className="text-xs mt-1 text-slate-500">{d.status} · {(d.created_at || "").slice(0, 16).replace("T", " ")}</div>
            </div>
            {d.status === "PENDIENTE" && (
              <Button data-testid={`mediation-close-${d.deal_id}`}
                className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0"
                onClick={() => act(`/admin/market/admin-deals/${d.deal_id}/close`, "Trato marcado como atendido")}>
                <Check className="w-4 h-4 mr-1" /> Atendido
              </Button>
            )}
          </Card>
        ))}

        {section === "payouts" && rows.map((p) => (
          <Card key={p.payout_id} testid={`admin-payout-${p.payout_id}`}>
            <div>
              <div className="text-white font-medium">
                {p.name} · ${Number(p.cash_mxn).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {p.credits} créditos (comisión {p.fee_pct}%) · {p.email || p.seller}
                {p.note ? ` · ${p.note}` : ""}
              </div>
              <div className="text-xs mt-1 text-slate-500">{p.status} · {(p.created_at || "").slice(0, 16).replace("T", " ")}</div>
            </div>
            {p.status === "PENDIENTE" && (
              <div className="flex gap-2 shrink-0">
                <Button data-testid={`payout-paid-${p.payout_id}`}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  onClick={() => act(`/admin/payouts/${p.payout_id}/paid`, "Cobro marcado como pagado", `¿Confirmas que ya le transferiste $${Number(p.cash_mxn).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN a ${p.name}?`)}>
                  <Check className="w-4 h-4 mr-1" /> Ya lo pagué
                </Button>
                <Button data-testid={`payout-reject-${p.payout_id}`}
                  className="bg-rose-600 hover:bg-rose-500 text-white"
                  onClick={() => act(`/admin/payouts/${p.payout_id}/reject`, "Cobro rechazado y créditos devueltos", `¿Rechazar el cobro de ${p.name}? Se le devolverán ${p.credits} créditos.`)}>
                  <X className="w-4 h-4 mr-1" /> Rechazar
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>
      </div>
    </Layout>
  );
}
